import { useQueryClient } from "@tanstack/react-query";
import { IntegrationType } from "@types";
import { captureSentryException } from "@utils";
import { useSendSlackTestMutation } from "src/hooks/mutations";
import { useGetSlackAuthUrl } from "src/hooks/queries";
import { getIntegrationQueryKey } from "../config/request-keys";

const useSlackOAuthFlow = (orgId: string) => {
  const authUrlQuery = useGetSlackAuthUrl(orgId);
  const queryClient = useQueryClient();

  const startOAuthFlow = async () => {
    try {
      const { data } = await authUrlQuery.refetch();

      if (data?.authUrl) {
        // Open OAuth URL in popup
        const popup = window.open(
          data.authUrl,
          "slack-oauth",
          "width=600,height=700,scrollbars=yes,resizable=yes"
        );

        // Listen for OAuth completion via postMessage
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type === `${IntegrationType.SLACK}_OAUTH_SUCCESS`) {
            const integrationKey = getIntegrationQueryKey(
              orgId,
              IntegrationType.SLACK
            );

            queryClient.invalidateQueries({
              queryKey: integrationKey,
            });

            queryClient.refetchQueries({
              queryKey: integrationKey,
            });

            window.removeEventListener("message", handleMessage);
          }
        };

        window.addEventListener("message", handleMessage);

        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkClosed);
            window.removeEventListener("message", handleMessage);
            queryClient.invalidateQueries({
              queryKey: getIntegrationQueryKey(orgId, IntegrationType.SLACK),
            });
            queryClient.refetchQueries({
              queryKey: getIntegrationQueryKey(orgId, IntegrationType.SLACK),
            });
          }
        }, 1000);

        return popup;
      }
    } catch (error) {
      console.error("Failed to start Slack OAuth flow:", error);
      captureSentryException(error, {
        type: IntegrationType.SLACK,
        message: "Failed to start Slack OAuth flow",
      });
      throw error;
    }
  };

  return {
    startOAuthFlow,
    isLoading: authUrlQuery.isLoading,
    error: authUrlQuery.error,
  };
};

export { useGetSlackAuthUrl, useSendSlackTestMutation, useSlackOAuthFlow };
